import { Button, Switch, TextField } from '@ads/components-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { tutorAIUpdateTokenLimitViewModelSchema } from '@/shared/api/generated/schemas/tutorAIUpdateTokenLimitViewModelSchema';
import { FormField } from '@/shared/ui/molecules';

import type { TokenLimitFormValues } from '../../../hooks/useTutorStudentLimit';
import { SurfaceCard } from '../../molecules/SurfaceCard';

/**
 * Regra violada por um campo de limite.
 *
 * O schema emite o código e a UI o resolve na mensagem traduzida. Guardar o código em vez
 * da chave de i18n mantém a checagem estática de chaves do `t()` — passar `error.message`
 * cru exigiria um cast que desliga essa checagem.
 *
 * Necessário porque o código de issue do Zod **não** distingue as regras: NaN e `1.5`
 * chegam os dois como `invalid_type`. Só a mensagem separa "vazio" de "não inteiro".
 */
enum ETokenLimitError {
  Required = 'required',
  NotInteger = 'notInteger',
  Negative = 'negative',
}

/**
 * Um campo de limite: inteiro `>= 0`, e **campo vazio é recusado**.
 *
 * Vazio não pode ser coagido a zero. Nesta tela `0` não é "vazio" — significa "sem limite
 * individual, vale o padrão global". Coagir faria a sequência "limpar o campo → salvar"
 * **remover** o limite do aluno, com o toast informando sucesso e o operador sem saber.
 * Remover um limite tem de ser deliberado: digitar `0`.
 *
 * O campo é registrado com `valueAsNumber`, então o vazio chega como `NaN` e cai no
 * `invalid_type_error` — não existe string vazia para o `Number('') === 0` transformar.
 */
const tokenLimitFieldSchema = z
  .number({
    required_error: ETokenLimitError.Required,
    invalid_type_error: ETokenLimitError.Required,
  })
  .int({ message: ETokenLimitError.NotInteger })
  .min(0, { message: ETokenLimitError.Negative });

/**
 * Schema do formulário, derivado do gerado.
 *
 * Os três limites viram obrigatórios: a API responde 400 em valor negativo, e no
 * formulário "não informado" não existe — ver {@link tokenLimitFieldSchema}.
 */
const tokenLimitFormSchema = tutorAIUpdateTokenLimitViewModelSchema.extend({
  dailyLimit: tokenLimitFieldSchema,
  weeklyLimit: tokenLimitFieldSchema,
  monthlyLimit: tokenLimitFieldSchema,
  enabled: z.boolean(),
});

/**
 * Ids dos campos.
 *
 * O `label` do ADS não usa `htmlFor` nem `aria-labelledby`, então a associação rótulo ↔
 * input vem do `FormField`: `htmlFor` no label, o mesmo valor em `id` no input.
 */
const FIELD_IDS = {
  dailyLimit: 'tutor-token-daily-limit',
  weeklyLimit: 'tutor-token-weekly-limit',
  monthlyLimit: 'tutor-token-monthly-limit',
} as const;

export interface TokenLimitFormProps {
  readonly defaultValues: TokenLimitFormValues;
  readonly onSave: (values: TokenLimitFormValues) => void;
  readonly isSaving: boolean;
}

/**
 * Formulário dos limites de token de um aluno.
 *
 * Zero é um valor válido e significativo — "sem limite individual, vale o padrão global" —
 * então o campo não é opcional nem tratado como vazio.
 */
export function TokenLimitForm({ defaultValues, onSave, isSaving }: TokenLimitFormProps) {
  const { t } = useTranslation('tutorAI');

  const form = useForm<TokenLimitFormValues>({
    resolver: zodResolver(tokenLimitFormSchema),
    defaultValues,
  });

  /**
   * Traduz o código da regra violada. Cada regra tem a sua mensagem: dizer "não pode ser
   * negativo" para um campo vazio ou para `1,5` informaria o operador errado.
   */
  function limitErrorMessage(code: string | undefined): string | undefined {
    switch (code) {
      case undefined:
        return undefined;
      case ETokenLimitError.Required:
        return t('student.limitForm.requiredError');
      case ETokenLimitError.NotInteger:
        return t('student.limitForm.integerError');
      case ETokenLimitError.Negative:
        return t('student.limitForm.negativeError');
      default:
        return t('student.limitForm.invalidError');
    }
  }

  /** Props de erro de um campo — spread condicional por causa de `exactOptionalPropertyTypes`. */
  function errorProps(code: string | undefined) {
    const message = limitErrorMessage(code);
    return message === undefined ? {} : { error: message };
  }

  return (
    <SurfaceCard
      title={t('student.limitForm.title')}
      description={t('student.limitForm.description')}
    >
      {/*
        `noValidate` deixa o Zod ser a única validação: com a validação nativa ligada, o
        `min={0}` aborta o submit antes do `onSubmit`, e o operador veria a bolha do
        navegador — sem tradução e fora do `FormField` — em vez da nossa mensagem. O
        `min={0}` fica só como limite do spinner do campo numérico.
      */}
      <form
        noValidate
        className="flex flex-col gap-mini p-xxs"
        onSubmit={form.handleSubmit(values => onSave(values))}
      >
        <div className="grid grid-cols-1 gap-mini sm:grid-cols-3">
          <FormField
            label={t('student.limitForm.daily')}
            htmlFor={FIELD_IDS.dailyLimit}
            {...errorProps(form.formState.errors.dailyLimit?.message)}
          >
            <TextField
              id={FIELD_IDS.dailyLimit}
              type="number"
              min={0}
              size="md"
              fullWidth
              hasError={Boolean(form.formState.errors.dailyLimit)}
              {...form.register('dailyLimit', { valueAsNumber: true })}
            />
          </FormField>
          <FormField
            label={t('student.limitForm.weekly')}
            htmlFor={FIELD_IDS.weeklyLimit}
            {...errorProps(form.formState.errors.weeklyLimit?.message)}
          >
            <TextField
              id={FIELD_IDS.weeklyLimit}
              type="number"
              min={0}
              size="md"
              fullWidth
              hasError={Boolean(form.formState.errors.weeklyLimit)}
              {...form.register('weeklyLimit', { valueAsNumber: true })}
            />
          </FormField>
          <FormField
            label={t('student.limitForm.monthly')}
            htmlFor={FIELD_IDS.monthlyLimit}
            {...errorProps(form.formState.errors.monthlyLimit?.message)}
          >
            <TextField
              id={FIELD_IDS.monthlyLimit}
              type="number"
              min={0}
              size="md"
              fullWidth
              hasError={Boolean(form.formState.errors.monthlyLimit)}
              {...form.register('monthlyLimit', { valueAsNumber: true })}
            />
          </FormField>
        </div>

        <Controller
          control={form.control}
          name="enabled"
          render={({ field }) => (
            <Switch
              label={t('student.limitForm.enabled')}
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />

        <p className="text-ctx-content-placeholder ts-caption-xxs">
          {t('student.limitForm.controlDisabledHint')}
        </p>

        <div className="flex justify-end">
          <Button type="submit" hierarchy="primary" size="md" isLoading={isSaving}>
            {t('student.limitForm.submit')}
          </Button>
        </div>
      </form>
    </SurfaceCard>
  );
}
